import type { Section } from '../types';

/**
 * Strings both shells use. Buttons say what happens. Errors say what went
 * wrong and what to do next. Sentence case everywhere.
 *
 * Spanish here is written with real accents. The ASCII discipline in this repo
 * applies to SQL only (see supabase/migrations), never to UI strings.
 */
export const common = {
  'app.guest.name': { es: 'Dia Bonito Concierge', en: 'Dia Bonito Concierge' },
  'app.ops.name': { es: 'Dia Bonito Concierge Ops', en: 'Dia Bonito Concierge Ops' },

  'action.save': { es: 'Guardar', en: 'Save' },
  'action.send': { es: 'Enviar', en: 'Send' },
  'action.cancel': { es: 'Cancelar', en: 'Cancel' },
  'action.back': { es: 'Volver', en: 'Back' },
  'action.retry': { es: 'Intentar de nuevo', en: 'Try again' },
  'action.close': { es: 'Cerrar', en: 'Close' },
  'action.continue': { es: 'Seguir', en: 'Continue' },
  'action.open_whatsapp': { es: 'Abrir WhatsApp', en: 'Open WhatsApp' },
  'action.take_photo': { es: 'Tomar foto', en: 'Take a photo' },
  'action.remove_photo': { es: 'Quitar la foto', en: 'Remove the photo' },
  'action.sign_out': { es: 'Salir', en: 'Sign out' },

  'lang.toggle_label': { es: 'Idioma', en: 'Language' },
  'lang.es': { es: 'Español', en: 'Spanish' },
  'lang.en': { es: 'Inglés', en: 'English' },
  'lang.show_original': { es: 'Ver el original', en: 'Show the original' },
  'lang.show_translation': { es: 'Ver la traducción', en: 'Show the translation' },

  'sync.saved_pending': {
    es: 'Guardado, pendiente de enviar',
    en: 'Saved, waiting to send',
  },
  'sync.sending': { es: 'Enviando', en: 'Sending' },
  'sync.all_sent': { es: 'Todo enviado', en: 'Everything is sent' },
  'sync.offline': { es: 'Sin señal. Sigue trabajando.', en: 'No signal. Keep working.' },
  'sync.pending_count': {
    es: '{count} sin enviar',
    en: '{count} waiting to send',
  },
  'sync.photo_pending': {
    es: 'La foto se envía sola cuando vuelva la señal',
    en: 'The photo sends itself when the signal comes back',
  },
  'sync.dead_count': {
    es: '{count} no entró. Pídele ayuda a tu jefe.',
    en: '{count} the island refused. Ask a host to look.',
  },
  'sync.dead_title': { es: 'Trabajos que no entraron', en: 'Writes the island refused' },
  'sync.dead_none': { es: 'Nada trabado. Todo va bien.', en: 'Nothing stuck. All good.' },
  'sync.dead_explain': {
    es: 'La isla no acepta estos trabajos. Lo demás ya se envió. Intenta otra vez, o bótalos si ya no sirven.',
    en: 'The island refuses these writes. Everything behind them has gone through. Try them again, or throw them away if they are no longer needed.',
  },
  'sync.dead_retry': { es: 'Intentar otra vez', en: 'Try again' },
  'sync.dead_discard': { es: 'Botar este', en: 'Throw this one away' },
  'sync.dead_retry_all': { es: 'Intentar todos otra vez', en: 'Try them all again' },
  'sync.dead_discard_all': { es: 'Botar todos', en: 'Throw them all away' },
  'sync.dead_confirm': { es: 'Se pierde. Confirma.', en: 'This is lost for good. Confirm.' },

  'status.ok': { es: 'Bien', en: 'Good' },
  'status.warn': { es: 'Ojo', en: 'Heads up' },
  'status.danger': { es: 'Problema', en: 'Problem' },
  'status.info': { es: 'Nota', en: 'Note' },
  'status.pending': { es: 'Pendiente', en: 'Pending' },
  'status.done': { es: 'Listo', en: 'Done' },

  'error.generic': {
    es: 'Algo falló. Intenta de nuevo en un momento.',
    en: 'Something went wrong. Try again in a moment.',
  },
  'error.offline_write_queued': {
    es: 'No hay señal, pero tu trabajo quedó guardado en el teléfono.',
    en: 'No signal, but your work is saved on the phone.',
  },
  'error.not_found': {
    es: 'Esta página no existe. Vuelve al inicio.',
    en: 'This page does not exist. Go back to the start.',
  },
  'error.session_expired': {
    es: 'Tu sesión se cerró. Entra de nuevo.',
    en: 'Your session ended. Sign in again.',
  },

  'empty.nothing_here': { es: 'Aquí no hay nada todavía.', en: 'Nothing here yet.' },
  'loading': { es: 'Un momento', en: 'One moment' },

  'install.prompt_title': {
    es: 'Pon la app en la pantalla de inicio',
    en: 'Add the app to your home screen',
  },
  'install.prompt_body': {
    es: 'Así abre rápido y funciona sin señal.',
    en: 'It opens faster and keeps working with no signal.',
  },

  'action.choose': { es: 'Escoger', en: 'Choose' },
  'action.open': { es: 'Abrir', en: 'Open' },
  'action.yes': { es: 'Sí', en: 'Yes' },
  'action.no': { es: 'No', en: 'No' },
} as const satisfies Section;
